import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import useTranslate from '../../Hooks/useTranslate';
import MultipleChipSelect from '../Inputs/MultipleChipSelect';
import Select from '../Inputs/Select';
import { CenteredFlexBox, ColumnFlexBox } from '../FlexBox';
import SegmentedControl from './SegmentedControl';
import BottomSheet from './BottomSheet';

const Field = function Field({ label, children }) {
  return (
    <ColumnFlexBox sx={{ gap: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#3a3a42' }}>
        {label}
      </Typography>
      {children}
    </ColumnFlexBox>
  );
};

/**
 * ReportFilterSheet — bottom drawer to change the report filters on mobile,
 * mirroring the desktop report filters (period, Branch/User scope, grouping,
 * branches, sections, operator). Each field is shown only when the active
 * report supports it.
 */
const ReportFilterSheet = function ReportFilterSheet({
  open = false,
  onClose = () => {},
  showPeriod = true,
  monthLabel = '',
  onMonthChange = () => {},
  canGoNext = false,
  showScope = false,
  scope = 'BRANCH',
  onScopeChange = () => {},
  showGrouping = false,
  grouping = 'D',
  onGroupingChange = () => {},
  showBranches = false,
  branchOptions = [],
  branches = [],
  onBranchesChange = () => {},
  showSections = false,
  sectionOptions = [],
  sections = [],
  onSectionsChange = () => {},
  showOperator = false,
  operatorOptions = [],
  operator = 0,
  onOperatorChange = () => {},
  onConfirm = () => {},
}) {
  const theme = useTheme();
  const translate = useTranslate();

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={translate('FILTERS')}
      closable
      maxHeight="90%"
    >
      <ColumnFlexBox
        className="scr"
        sx={{ mt: 0.5, px: 2.5, pb: 1, gap: 2, overflowY: 'auto' }}
      >
        {showPeriod ? (
          <Field label={translate('PERIOD')}>
            <CenteredFlexBox
              sx={{
                justifyContent: 'space-between',
                bgcolor: 'background.default',
                borderRadius: '12px',
                px: 1,
              }}
            >
              <IconButton
                aria-label="previous month"
                onClick={() => onMonthChange(-1)}
              >
                <ChevronLeftRoundedIcon />
              </IconButton>
              <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                {monthLabel}
              </Typography>
              <IconButton
                aria-label="next month"
                disabled={!canGoNext}
                onClick={() => onMonthChange(1)}
              >
                <ChevronRightRoundedIcon />
              </IconButton>
            </CenteredFlexBox>
          </Field>
        ) : null}

        {showScope ? (
          <Field label={translate('VIEW')}>
            <SegmentedControl
              value={scope}
              onChange={onScopeChange}
              options={[
                { value: 'BRANCH', label: translate('BRANCH') },
                { value: 'USER', label: translate('USER') },
              ]}
            />
          </Field>
        ) : null}

        {showGrouping ? (
          <Field label={translate('GROUPING')}>
            <SegmentedControl
              value={grouping}
              onChange={onGroupingChange}
              options={[
                { value: 'D', label: translate('DAY') },
                { value: 'W', label: translate('WEEK') },
                { value: 'M', label: translate('MONTH') },
              ]}
            />
          </Field>
        ) : null}

        {showBranches ? (
          <MultipleChipSelect
            label={translate('BRANCHES')}
            size="small"
            options={branchOptions}
            values={branches}
            onChange={onBranchesChange}
          />
        ) : null}

        {showSections ? (
          <MultipleChipSelect
            label={translate('SECTIONS')}
            size="small"
            options={sectionOptions}
            values={sections}
            onChange={onSectionsChange}
          />
        ) : null}

        {showOperator ? (
          <Field label={translate('OPERATOR')}>
            <Select
              name="report-operator"
              size="small"
              options={operatorOptions}
              value={operator}
              onChange={onOperatorChange}
            />
          </Field>
        ) : null}
      </ColumnFlexBox>

      <Box
        sx={{
          px: 2.5,
          pt: 1.5,
          pb: 'calc(env(safe-area-inset-bottom) + 16px)',
          flex: 'none',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={onConfirm}
          sx={{
            borderRadius: '14px',
            py: 1.25,
            textTransform: 'none',
            fontWeight: 700,
            background: theme.custom.mobile.brandGradient,
          }}
        >
          {translate('CONFIRM')}
        </Button>
      </Box>
    </BottomSheet>
  );
};

export default ReportFilterSheet;
